import { ColDef, FilterModel, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'

import {
  DEFAULT_TASK_TABLE_COLUMNS,
  FilterType,
  TaskManagerEnumerations,
  User,
  ViewColumn,
  ViewTask,
  ViewTasksFilter,
  getFilterOperator,
  getFilterOptions,
  getFilterValue,
  localizeString,
} from '@flowx/core-sdk'

import { ANGULAR_CUSTOM_AG_GRID_FILTER_TYPE } from './task.constants'

export const GET_COLUMN_DEFS = (view: any, enumerations: TaskManagerEnumerations): any[] =>
  view.columns
    .sort((a: ViewColumn, b: ViewColumn) => a.order - b.order)
    .map((column: ViewColumn) => {
      const colDef: ColDef = {
        headerName: localizeString(column.displayName),
        field: column.keyName,
        sortable: true,
        suppressMovable: true,
        flex: 1,
        comparator: () => 0,
        ...getFilterOptions(column, enumerations, ANGULAR_CUSTOM_AG_GRID_FILTER_TYPE),
        filterValueGetter: (params: ValueGetterParams) =>
          GET_ALWAYS_VALUE_PASS(params.api.getFilterModel()[params.colDef.field]),
        valueGetter: (params: ValueGetterParams) =>
          params.data ? params.data[column.keyName] : undefined,
        cellRenderer: (params: ICellRendererParams) => (params.value == null ? '' : params.value),
      }
      if (column.sort?.active) {
        colDef.sort = column.sort.direction === 'ASC' ? 'asc' : 'desc'
        colDef.sortingOrder = ['asc', 'desc']
      }
      return colDef
    })

// we need to return a value that will always pass the filter because client side grid filtering cannot be disabled, filtering is done server side,
const GET_ALWAYS_VALUE_PASS = (filter: any): any => {
  switch (filter.filterType) {
    case 'text':
      return GET_TEXT_ALWAYS_PASS_VALUE(filter)
    case 'number':
      return GET_NUMBER_ALWAYS_VALUE_PASS(filter)
    case 'date':
      return GET_DATE_ALWAYS_PASS_VALUE(filter)
    default:
      return ''
  }
}

const GET_TEXT_ALWAYS_PASS_VALUE = (filter: any): any => {
  switch (filter.type) {
    case 'notEqual':
      return ''
    case 'notContains':
      return ''
    default:
      return filter.filter
  }
}

const GET_NUMBER_ALWAYS_VALUE_PASS = (filter: any): number => {
  switch (filter.type) {
    case 'lessThan':
      return filter.filter - 1
    case 'greaterThan':
      return filter.filter + 1
    default:
      return filter.filter
  }
}

const GET_DATE_ALWAYS_PASS_VALUE = (filter: any): Date => {
  let date: Date
  switch (filter.type) {
    case 'lessThan':
      date = new Date(filter.dateFrom)
      date.setDate(date.getDate() - 1)
      return date
    case 'greaterThan':
      date = new Date(filter.dateFrom)
      date.setDate(date.getDate() + 1)
      return date
    default:
      return filter.dateFrom
  }
}

export const MAP_FILTERS = (filterModel: FilterModel, columns: ViewColumn[]): ViewTasksFilter[] => {
  const filters: ViewTasksFilter[] = []
  Object.keys(filterModel).forEach((key: string) => {
    const column = columns.find((col) => col.keyName === key)
    if (
      filterModel[key].filter !== null &&
      ((column.keyType !== FilterType.enumType &&
        column.keyName !== DEFAULT_TASK_TABLE_COLUMNS.stage.id) ||
        filterModel[key].filter.length)
    ) {
      filters.push({
        keyName: key,
        keyType: column.keyType,
        columnType: column.columnType,
        operator: getFilterOperator(filterModel[key].type),
        value: getFilterValue(filterModel[key], column),
      })
    }
  })
  return filters
}

export const MAP_TASKS_FOR_GRID = (tasks: Array<ViewTask>): any[] =>
  tasks.map((task: ViewTask): any => ({
    ...task.columnValues,
    agGridId: task.tokenUuid,
  }))

export const GET_EMPTY_DATA_SOURCE = (index: number): Partial<ViewTask>[] => {
  const result: Partial<ViewTask>[] = []
  for (let i = 0; i < index; i++) {
    result.push({ tokenUuid: i.toString() })
  }
  return result
}

export const MAP_ASSIGNEES = (assignees: User[]): { value: string; label: string }[] =>
  assignees.map((assignee) => ({
    value: assignee.username,
    label: `${assignee.firstName} ${assignee.lastName} (${assignee.username}) ${
      !assignee.enabled ? localizeString('@@sys_tm_assignee_deactivated') : ''
    }`,
  }))
